import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography, TextField,
  IconButton,
  Divider,
  MenuItem,
  Paper,
  Button,
  Select,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  useTheme,
  Chip
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { jwtDecode } from "jwt-decode";
import { useAccessToken } from "../../../auth/useAccessToken";
import {
  fetchUserAccess, fetchRegionsAndPlants, fetchMrpControllers,
  raiseAccessRequest, raiseAccessRequestForUsers
} from "../../../services/OutboundOrders/UserRolesServices";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from '@mui/icons-material/Done';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { PDFDocument, StandardFonts } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import TopSection from "../../../CommonComponents/TopSection";
import SectionSpinner from "../../../CommonComponents/common/Loader/SectionSpinner";

// First dataset (array of arrays)
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export const NDA_CONTENT = ({ today, username }) => [
  <Typography key="p1" variant="body2" paragraph>
    This Non‑Disclosure Agreement <strong>(Agreement)</strong> is entered into effective as of{" "}
    <strong>{today}</strong> between{" "}
    <strong>The Coca‑Cola Company located at One Coca‑Cola Plaza NW, Atlanta, GA 30313</strong>{" "}
    and <strong>{username}</strong>, each of which will be referred to as a Party and collectively
    as Parties.
  </Typography>,

  <Typography key="p2" variant="body2" paragraph>
    The term <strong>“Party”</strong> will include an affiliate of that Party. For purposes of this
    Agreement, an <strong>“Affiliate”</strong> of a Party means (i) direct and indirect subsidiaries;
    (ii) any entity that such Party directly or indirectly controls; or (iii) in the case of TCCC,
    any authorized bottler or wholly‑owned subsidiary.
  </Typography>,

  <Typography key="p3" variant="body2" paragraph>
    The Parties may disclose or share with each other information, documents, and other confidential
    or proprietary matters of the disclosing Party <strong>(Information)</strong> in connection
    with <strong>Confidential Information</strong>.
  </Typography>,

  <Typography key="h1" variant="body2" fontWeight={600} gutterBottom>
    Confidential Information includes:
  </Typography>,

  <ol key="list1" type="a" style={{ paddingLeft: "1.5rem", marginTop: 0 }}>
    <li>
      <Typography variant="body2">
        Information that the disclosing Party marks or designates as confidential.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Information that is not marked but would reasonably be considered confidential.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        The fact that this Agreement exists and the Parties are cooperating.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Any notes, analyses, summaries, or materials derived from Confidential Information.
      </Typography>
    </li>
  </ol>,

  <Typography key="h2" variant="body2" fontWeight={600} gutterBottom>
    Obligations of the Receiving Party:
  </Typography>,

  <ol key="list2" type="a" style={{ paddingLeft: "1.5rem", marginTop: 0 }}>
    <li>
      <Typography variant="body2">
        Restrict disclosure of Confidential Information to employees with a need to know.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Not disclose Confidential Information to third parties except as permitted.
      </Typography>

      <ol type="i" style={{ paddingLeft: "1.5rem" }}>
        <li>
          <Typography variant="body2">
            Individuals assisting with the stated purpose.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Individuals informed of the confidential nature of the Information.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Individuals bound by confidentiality obligations no less restrictive than this Agreement.
          </Typography>
        </li>
      </ol>
    </li>
    <li>
      <Typography variant="body2">
        Use Confidential Information solely for the agreed purpose.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Return or destroy Confidential Information upon written request, except one archival copy as
        required by law.
      </Typography>
    </li>
    <li>
      <Typography variant="body2">
        Promptly notify the disclosing Party of any unauthorized disclosure.
      </Typography>
    </li>
  </ol>,

  <Typography key="p4" variant="body2" paragraph>
    This Agreement is governed by the laws of the <strong>State of Georgia</strong>, and the courts
    located in <strong>Atlanta, Georgia</strong> shall have exclusive jurisdiction.
  </Typography>,

  <Typography key="p5" variant="body2" paragraph>
    A breach of this Agreement may cause irreparable harm for which monetary damages would be
    insufficient, and injunctive relief may be sought.
  </Typography>,

  <Typography key="p6" variant="body2" paragraph>
    This Agreement represents the <strong>entire agreement</strong> between the Parties and
    supersedes all prior communications or agreements.
  </Typography>,

  <Typography key="p7" variant="body2" paragraph>
    The initial term of this Agreement shall be <strong>three (3) years</strong>, renewing annually
    unless terminated in writing by either Party.
  </Typography>,
];

const NDA_PDF_TEXT = ({ today, username }) => [
  `NON-DISCLOSURE AGREEMENT`,

  `1. This Non-Disclosure Agreement (Agreement) is entered into effective as of ${today} between The Coca-Cola Company located at One Coca-Cola Plaza NW, Atlanta, GA 30313 and ${username}, each of which will be referred to as a Party and collectively as Parties. The term “Party” will include an affiliate of that Party. For purposes of this Agreement, an “Affiliate” of a Party means (i) direct and indirect subsidiaries; (ii) any entity that such Party directly, or indirectly through one or more intermediaries, controls, or is controlled by or is under common control with; or (iii) in the case of TCCC, any bottler with whom TCCC has a license, alliance, or distribution agreement and any wholly-owned subsidiary thereof. The term "control" means ownership of more than 50%, giving an entity the possession, directly or indirectly, of the power to direct or cause the direction of the management and policies of another entity.`,

  `2. The Parties may disclose or share with each other information, documents, and other confidential or proprietary matters of the disclosing Party (Information) in connection with Confidential Information.`,

  `3. The following Information will be considered Confidential Information:`,
  `a. Information that the disclosing Party marks or designates as confidential;`,
  `b. Information that is not marked or designated as being confidential, but that under the circumstances a reasonable person would believe to be confidential;`,
  `c. The fact that this Agreement exists and that the Parties are cooperating with each other for the Purpose; and`,
  `d. Any notes, analyses, compilations, studies, summaries, and other materials, however documented, containing or based, in whole or in part, on any Confidential Information.`,

  `4. Each Party receiving Confidential Information will:`,
  `a. restrict the disclosure of Confidential Information only to its employees who have a need to know the Confidential Information to achieve the Purpose;`,
  `b. not disclose the Confidential Information to any third party; provided however that each Party may disclose Confidential Information to its affiliates, directors, attorneys, and financial advisors who:`,
  `c. only use the Confidential Information for the Purpose;`,
  `d. return all tangible Information (including any copies thereof), and erase all electronic or other intangible forms of the Information, within 30 days of the receipt of a written request from the disclosing Party to do so, provided however that the receiving Party may retain one archival copy to the extent required to comply with its legal obligations; and`,
  `e. give prompt written notice to the disclosing Party of any unauthorized use or disclosure of the Confidential Information, and reasonably assist the disclosing Party in remedying each unauthorized use or disclosure.`,

  `5. The obligations in Section 4 above will not apply to Confidential Information that:`,
  `a. is, or subsequently becomes, available to the public through no breach of the receiving Party’s obligations hereunder;`,
  `b. the receiving Party can show that such Information was previously known to it as a matter of record at the time of receipt;`,
  `c. is subsequently and lawfully obtained from a third party who has obtained the Confidential Information through no breach of the receiving Party’s obligations hereunder; or`,
  `d. is disclosed to a third party by the disclosing Party without a corresponding obligation of confidentiality.`,

  `6. Additionally, a receiving Party will not be in breach of its obligations if it discloses Confidential Information as required by a government agency or by operation of law; provided however that the receiving Party (to the extent permitted by applicable law):`,
  `a. notifies the disclosing Party prior to disclosure to enable the disclosing Party to seek to oppose or restrict the disclosure;`,
  `b. reasonably cooperates with any such attempt by the disclosing Party to oppose or restrict the disclosure or to seek a protective order; and`,
  `c. only discloses such Confidential Information that is required to be disclosed by applicable law.`,

  `7. Each Party agrees not to publish or use any advertising, sales promotion, mailing or publicity matter that mentions or implies the names of the other Party or its affiliates or authorized bottlers without the prior written consent of the other Party.`,

  `8. This Agreement does not constitute a grant of any right or license of any intellectual property rights (e.g., patents, trademarks, copyrights, and trade secrets) of either Party, and each Party will remain the owner of its Confidential Information and all intellectual property rights in its Confidential Information.`,

  `9. No agreement regarding the Purpose will be deemed to exist unless and until a definitive written agreement regarding the Purpose has been signed by each of the Parties. Neither Party is obligated to enter into a definitive written agreement, and neither Party is precluded from considering the same or similar goods or services from third parties. Neither Party will be obligated to:`,
  `a. pay or otherwise provide a benefit to the other Party;`,
  `b. take or refrain from taking any action, except as expressly set forth in this Agreement; or`,
  `c. disclose any Information.`,

  `10. Each disclosing Party represents that it has the lawful right to disclose any Information that it discloses to the receiving Party. Each Party makes no further representations of any kind with respect to any Information that it discloses, and any such Information is provided on an “as is” basis.`,

  `11. This Agreement and its interpretation, and any disputes related to or arising out of this Agreement, will be governed by the laws of the State of Georgia. The courts in the city of Atlanta, Georgia having jurisdiction will adjudicate any such disputes. Each party hereby irrevocably submits to the exclusive jurisdiction of such courts in any such suit, action or proceeding, and each party consents to the venue of such courts.`,

  `12. A breach of this Agreement by a receiving Party will cause the disclosing Party irreparable injury for which monetary damages will not provide adequate compensation and, in addition to any other remedy, the disclosing Party will be entitled to seek injunctive relief against such breach or threatened breach without proving actual damages.`,

  `13. Each Party, in connection with this Agreement, will not engage with:`,
  `a. the government of, or any entity, group or individual within, any country that is the target of any laws administered by the U.S. Office of Foreign Assets Control, Department of the Treasury (OFAC) or any other governmental entity around the world imposing economic sanctions (collectively Embargoed Country); and`,
  `b. any government, entity, group, or individual who is named on the OFAC List of Specially Designated Nationals and Blocked Persons or other similar lists maintained by any governmental entity (collectively Sanctioned Party).`,

  `14. Each Party represents and warrants that it is not:`,
  `a. a Sanctioned Party;`,
  `b. owned or controlled by, or acting for or on behalf of, a Sanctioned Party; or`,
  `c. directly or indirectly owned, controlled by, or acting on behalf of an Embargoed Country.`,
  `Should a Party become designated a Sanctioned Party, or become associated, controlled, or owned by aSanctioned Party or Embargoed Country, any agreement between the Parties will terminate automatically.`,

  `15. Neither Party will be entitled to assign this Agreement to a third party.`,

  `16. This Agreement may be executed in any number of counterparts (including without limitation signature via DocuSign or similar e-signature service or by facsimile transmission), each of which will be deemed an original, but all of which taken together will constitute one single agreement between the Parties. The person signing on behalf of each Party represents that he or she is authorized to execute this Agreement on behalf of such Party and has the authority to bind such Party to the terms and conditions of this Agreement.`,

  `17. This Agreement represents the entire agreement of the Parties and it supersedes all prior communications, agreements, and understandings between the Parties relating to the subject matter hereof. This Agreement may not be modified, amended, or waived except by a written agreement signed by each Party. If a court of competent jurisdiction determines that a provision of this Agreement is unenforceable, this Agreement will be interpreted as if such unenforceable provision is not part of this Agreement.`,

  `18. The initial term of this Agreement will be for a term of three (3) years. Thereafter, the term of this Agreement will renew for successive one-year periods until terminated by either Party. Either Party may terminate this Agreement by providing at least 30 days’ prior written notice to the individual signing on behalf of the other Party. Sections 4, 5, 6, 7 and 8 will survive termination of this Agreement and will continue until the information is no longer considered Confidential Information; provided however that with respect to any Confidential Information that constitutes a trade secret, Sections 4, 5, 6, 7, and 8 will continue to apply until such time as that Confidential Information is no longer protected as a trade secret under applicable law.`,
];

const initialRequestState = {
  applicationType: "Command Center",
  region: [],
  plant: [],
  //mrpController: [],
  confidentialAccess: [],
};

const MultiSelectCheckbox = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "Select one or more",
  width = 200,
}) => {
  const theme = useTheme();
  const safeValue = Array.isArray(value) ? value : [];
  const allSelected = safeValue.length === options.length && options.length > 0;
  const selectAllValue = "SELECT_ALL_OPTION";

  const handleSelectAll = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const optionValues = options.filter(o => !o.disabled).map(o => o.value);
    const newSelection = allSelected ? [] : optionValues;
    onChange({ target: { value: newSelection } });
  };

  const handleItemChange = (event) => {
    const selectedValues = event.target.value;
    const isSelectAllToggled = selectedValues.includes(selectAllValue);
    if (isSelectAllToggled) {
      if (allSelected) onChange({ target: { value: [] } });
      else onChange({ target: { value: options.filter(o => !o.disabled).map(o => o.value) } });
    } else onChange(event);
  };

  return (
    <FormControl size="small" fullWidth disabled={disabled} sx={{ width }}>
      <InputLabel id={`${label}-label`}>{label}</InputLabel>
      <Select labelId={`${label}-label`} id={label} multiple value={Array.isArray(value) ? value : []} onChange={handleItemChange} label={label}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return (
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {placeholder}
              </Typography>
            );
          }

          // Map selected values to their labels
          const selectedLabels = options
            .filter(opt => selected.includes(opt.value))
            .map(opt => opt.label);

          if (selectedLabels.length === options.length) {
            return "All Selected";
          }
          if (selectedLabels.length > 2) {
            return `${selectedLabels.slice(0, 2).join(", ")}, +${selectedLabels.length - 2} more`;
          }
          return selectedLabels.join(", ");
        }}
        sx={{ minWidth: width, "& .MuiSelect-select": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: width - 30 } }}
      >
        <MenuItem value={selectAllValue} onClick={handleSelectAll}>
          <Checkbox checked={allSelected} indeterminate={safeValue.length > 0 && safeValue.length < options.length} />
          <ListItemText primary="Select All" primaryTypographyProps={{ fontWeight: 600 }} />
        </MenuItem>
        <Divider />
        {options.map(opt => (
          <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
            <Checkbox checked={safeValue.includes(opt.value)} disabled={opt.disabled} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const AccessRequestForm = () => {
  const breadcrumbArray = ["User Access Management", "New Request"];
  const [requestedFor, setRequestedFor] = useState("");
  const [requestedForUserName, setRequestedForUserName] = useState("");
  const [onBehalfWarned, setOnBehalfWarned] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(initialRequestState);
  const [fullyAccessibleRegions, setFullyAccessibleRegions] = useState([]);
  //const [availableMrpControllers, setAvailableMrpControllers] = useState([]);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [fileName, setFileName] = useState("");
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [ndaDecision, setNdaDecision] = useState("Not Accepted");
  const [openNdaModal, setOpenNdaModal] = useState(false);
  const [ndaForm, setNdaForm] = useState({
    effectiveDate: "",
    disclosingParty: "The Coca-Cola Company",
    receivingParty: "",
    purposeOfDisclosure: "",
    partyLegalName: "",
    signBy: "",
    signName: "",
    signTitle: "",
    signDate: "",
    noticeAddress: "",
    noticeEmail: ""
  });
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState("")
  const [userAccessResp, setUserAccessResp] = useState(null);
  const [decoded, setDecoded] = useState(null);
  const [showPreviewSummary, setShowPreviewSummary] = useState(false);
  const [previewSummary, setPreviewSummary] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" or "error"
  const [submitting, setSubmitting] = useState(false);

  const [fieldsData, setFieldsData] = useState({
    applications: [],
    regions: [],
    plants: [],
    //mrpControllers: [],
    confidentialFields: []
  });
  const INPUT_WIDTH = 450;
  const accessToken = useAccessToken();
  const today = new Date().toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

  const { application, region, plant, /*mrpController,*/ confidentialAccess } = currentRequest;

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    try {
      const parsed = jwtDecode(accessToken);
      setDecoded(parsed);
    } catch (err) {
      console.error('Failed to decode token:', err);
    }
  }, [accessToken]);

  useEffect(() => {
    const fetchRegionsAndPlantsMappings = async () => {
      try {
        const regionsAndPlantsMapping = await fetchRegionsAndPlants();
        const userAccessResp = await fetchUserAccess();
        //  const userAccessResp = {
        //   "ctAppRoles": [
        //     "CT-DEV-USER",
        //     "CT-BOM",
        //     "CT-INVENTORY"
        //   ],
        //   "regionPlantMapping": {
        //     "EMEA": [
        //       "0120",
        //       "0125"
        //     ],
        //     "AMERICAS": [
        //       "0047"
        //     ]
        //   },
        // mrpControllerList: ["101", "102"],
        // };
        setUserAccessResp(userAccessResp);

        // const regionsAndPlantsMapping = [
        //   [
        //     "0047",
        //     "CPS",
        //     true,
        //     "AMERICAS"
        //   ],
        //   [
        //     "0079",
        //     "CPS",
        //     true,
        //     "APAC"
        //   ],
        //   [
        //     "0103",
        //     "CPS",
        //     true,
        //     "AMERICAS"
        //   ],
        //   [
        //     "0120",
        //     "CPS",
        //     true,
        //     "EMEA"
        //   ],
        //   [
        //     "0125",
        //     "CPS",
        //     true,
        //     "EMEA"
        //   ],
        //   [
        //     "0384",
        //     "CPS",
        //     true,
        //     "APAC"
        //   ],

        //   [
        //     "0907",
        //     "CPS",
        //     true,
        //     "EMEA"
        //   ],

        // ];
        // const userAccessResp = {
        //   "ctAppRoles": [
        //     "CT-DEV-USER",
        //     "CT-BOM"
        //   ],
        //   "regionPlantMapping": {
        //     "EMEA": [
        //       "0120",
        //       "0125"
        //     ],
        //     "AMERICAS": [
        //       "0047", "0103"
        //     ]
        //   },
        //mrpController: ["001", "002", "003"],
        // };
        // Target structure
        const MOCK_ACCESS_DATA = {
          applications: ["Command Center", "Data Hub", "Material Planning"],
          regions: [],
          plants: [],
          confidentialFields: [
            { value: "BOM", label: "BOM" },
            { value: "MATERIAL MASTER", label: "MATERIAL MASTER" },
            { value: "FINANCE", label: "FINANCE" },
            { value: "INVENTORY", label: "INVENTORY" }
          ]
        };
        const regionSet = new Set();

        regionsAndPlantsMapping.forEach(([plantFullName, region]) => {
          regionSet.add(region);

          if (!MOCK_ACCESS_DATA.plants[region]) {
            MOCK_ACCESS_DATA.plants[region] = [];
          }

          MOCK_ACCESS_DATA.plants[region].push({
            value: plantFullName,      // ✅ full value
            label: plantFullName       // ✅ full label
          });
        });

        // Store fully accessible regions in state for reuse
        const fullRegions = Object.entries(userAccessResp.regionPlantMapping)
          .filter(([region, userPlants]) => {
            const allPlants = MOCK_ACCESS_DATA.plants[region] || [];
            return allPlants.every(p => userPlants.includes(p));
          })
          .map(([region]) => region);
        setFullyAccessibleRegions(fullRegions);

        MOCK_ACCESS_DATA.regions = Array.from(regionSet).map(regionName => ({
          value: regionName,
          label: regionName
        }));

        // Object.keys(MOCK_ACCESS_DATA.plants).forEach(regionName => {
        //   MOCK_ACCESS_DATA.plants[regionName] = MOCK_ACCESS_DATA.plants[regionName].map(plantCode => ({
        //     value: plantCode,
        //     label: plantCode
        //   }));
        // });
        setFieldsData(MOCK_ACCESS_DATA);
      } catch (err) {
        console.error(" API failed:", err);
        setSnackbarMessage("Error fetching Regions and Plants");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
    fetchRegionsAndPlantsMappings();
  }, []);

  // useEffect(() => {
  //   const getMrpControllersForPlants = async () => {
  //     if (currentRequest.plant.length === 0) {
  //       setAvailableMrpControllers([]);
  //       return;
  //     }
  //     try {
  //       const plantCodes = currentRequest.plant.map(p => p.split("-")[0]); // Extract plant codes
  //       const mrpControllersForPlants = await fetchMrpControllers(plantCodes);
  //       // const mrpControllersForPlants = {
  //       //   mrpControllerList: [
  //       //     "null-null",
  //       //     "001-Initial MRP Cntrl",
  //       //     "101-Liquid Filling",
  //       //     "102-Liquid Mixing",
  //       //     "125-NEW PRODUCTS",
  //       //     "133-Liq_Geral",
  //       //     "134-Liq_Alcoolico",
  //       //     "135-Liq_Coca-Cola Zero",
  //       //     "136-Liq_Coca-Cola",
  //       //     "137-Liq_MTO",
  //       //     "138-Liq_Emulsao",
  //       //     "139-Liq_Corantes",
  //       //     "141-Liq_Filtracao",
  //       //     "200-Liquid Batch",
  //       //     "201-Ingredients",
  //       //     "204-Monica Palacios",
  //       //     "205-Maria Machuca",
  //       //     "207-Yesenia Perez",
  //       //     "209-UISS/BCP",
  //       //     "252-Gisela M. Marin",
  //       //     "253-Isabella Novellino",
  //       //     "253-Kyssya Santos",
  //       //     "254-Lorena Vargas",
  //       //     "255-Laura Endoqui",
  //       //     "257-Karla Melissa Fall",
  //       //     "259-Cristian F. Gonzal",
  //       //     "260-BCP Sophia Dias",
  //       //     "260-Luisa A. Vasquez",
  //       //     "261-Aisha Nunes",
  //       //     "262-Diana Hernandez",
  //       //     "263-Melquiades Costa",
  //       //     "265-Egilda Galea",
  //       //     "266-Mariana Sanchez",
  //       //     "268-Cristina Li",
  //       //     "269-Polyana Escossio",
  //       //     "270-Oziel Cavalcante",
  //       //     "271-Miguel Viso",
  //       //     "300-Dry_Low Volume",
  //       //     "301-Dry_Mid Volume",
  //       //     "303-Dry_High Volume",
  //       //     "304-Dry_MTO",
  //       //     "400-WIP's",
  //       //     "401-MFI",
  //       //     "517-Dry_Still_SI_Par",
  //       //     "619-BCP Initial Ctrl",
  //       //     "701-PC Ayline Castillo",
  //       //     "731-PC Alessandra Font",
  //       //     "800-Phantoms BZ",
  //       //     "900-Maintenance MRO",
  //       //     "998-In/Out Materials",
  //       //     "999-Obsolete Materials"
  //       //   ]
  //       // };
  //       // Disable MRP controllers the user already has access to:
  //       const userMrpAccess = userAccessResp?.mrpControllerList || [];

  //       const formatted = mrpControllersForPlants.mrpControllerList.map(item => {
  //         const mrpCode = item.split("-")[0];
  //         const isDisabled = userMrpAccess.includes(mrpCode);
  //         return {
  //           value: item,
  //           label: item,
  //           disabled: isDisabled
  //         };
  //       });
  //       setAvailableMrpControllers(formatted);
  //     } catch (err) {
  //       console.error("Error fetching MRP controllers:", err);
  //       setSnackbarMessage("Error fetching MRP controllers");
  //       setSnackbarSeverity("error");
  //       setOpenSnackbar(true);
  //       setAvailableMrpControllers([]);
  //     }
  //   };
  //   getMrpControllersForPlants();
  // }, [currentRequest.plant, userAccessResp?.mrpControllerList]);

  const availablePlants = useMemo(() => {
    if (region.length === 0 || !fieldsData.plants) return [];
    let plants = [];

    const isGlobalSelected = region.includes("GLOBAL");
    if (isGlobalSelected) {
      plants = Object.values(fieldsData.plants).flat();
    } else {
      plants = region.flatMap(r => fieldsData.plants[r] || []);
    }
    return plants; // already array of {value, label, disabled}
  }, [region, fieldsData.plants]);

  useEffect(() => {
    // Whenever fieldsData updates, clear region/plant selections so dropdown recalcs
    setCurrentRequest(prev => ({
      ...prev,
      region: [],
      plant: []
    }));
  }, [fieldsData]);
  if (!decoded) {
    return <Typography>Loading token...</Typography>;
  }
  const email = decoded.unique_name || "";
  const username = `${decoded.given_name || ""} ${decoded.family_name || ""}`;

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleRequestedForChange = (e) => {
    const value = e.target.value;
    setRequestedFor(value);

    // Show snackbar only once when user starts typing
    if (value.trim().length > 0 && !onBehalfWarned) {
      setSnackbarMessage(
        "You are not allowed to request restricted access onbehalf of another user"
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setOnBehalfWarned(true);
    }
    // Reset warning if user clears the field
    if (value.trim().length === 0) {
      setOnBehalfWarned(false);
    }
  };

  const handleRequestedForUserNameChange = (e) => {
    const value = e.target.value;
    setRequestedForUserName(value);

    // Show snackbar only once when user starts typing
    if (value.trim().length > 0 && !onBehalfWarned) {
      setSnackbarMessage(
        "You are not allowed to request restricted access onbehalf of another user"
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      setOnBehalfWarned(true);
    }
    // Reset warning if user clears the field
    if (value.trim().length === 0) {
      setOnBehalfWarned(false);
    }
  };

  const formatValue = (value, fallback, allOptions) => {
    //console.log('value', value);
    //console.log('allOptions', allOptions);
    if (Array.isArray(value)) {
      if (value.length === 0) return fallback;
      else if (allOptions && value.length === allOptions.length) return `All ${fallback.replace("No ", "")}s`;
      else if (value.length > 3) return `${value.slice(0, 3).join(", ")}, +${value.length - 3} more`;
      return value.join(", ");
    }
    return value || fallback;
  };

  const generateSummary = (req) => {
    //console.log("req", req);
    //const appStr = req.application || "No Application";
    //if (appStr === "No Application") return "";
    let summary = `Access requested for Application: **Command Center**`;
    //let summary = `Access requested for Application: **${appStr}**`;
    summary += `\n\n--- Access Level ---`;
    summary += `\n- Region(s): ${formatValue(req.region, "No Region", fieldsData.regions)}`;
    summary += `\n- Plant(s): ${formatValue(req.plant, "No Plant")}`;
    //summary += `\n- MRP Controller(s): ${formatValue(req.mrpController, "No MRP Controller")}`;
    summary += `\n\n--- Confidential Access Level ---`;
    summary += `\n- NDA Status: ${ndaDecision}`;
    const confStr = formatValue(req.confidentialAccess, "None", fieldsData.confidentialFields);
    summary += `\n- Fields: ${confStr}`;
    return summary;
  };

  const handleFileDownload = () => {
    const fileUrl = "/files/TCCC-Form_Of_NDA(23June2022).docx";
    const element = document.createElement("a");
    element.href = fileUrl;
    element.download = "TCCC-Form_Of_NDA(23June2022).docx";
    document.body.appendChild(element);     // Append the link to the DOM
    element.click();                        // Programmatically trigger click
    document.body.removeChild(element);     // Cleanup
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileUploaded(true);
      setFileName(file.name);
      setSnackbarMessage("File uploaded successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSignatureFile(file);
    setSignaturePreview(URL.createObjectURL(file));
  };

  const handleNdaChange = (field) => (e) => {
    setNdaForm(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleMultiSelectChange = (field) => (e) => {
    const selectedValues = Array.isArray(e.target.value) ? e.target.value : [];
    setCurrentRequest((prev) => {
      const newRequest = { ...prev, [field]: selectedValues };
      if (field === "region") {
        newRequest.plant = [];
        //newRequest.mrpController = [];
      }
      else if (field === "plant") {
        //newRequest.mrpController = [];
      }
      return newRequest;
    });
  };

  const handlePreview = async () => {
    //if (application.length === 0) return;
    const summary = generateSummary(currentRequest);
    setShowPreviewSummary(true);
    setPreviewSummary(summary);
  };

  const normalizeText = (text) =>
    text
      .replace(/\s+/g, " ")   // collapse all whitespace
      .replace(/\n/g, " ")    // remove line breaks
      .trim();

  const getInitials = (fullName = "") =>
    fullName
      .split(" ")
      .filter(Boolean)
      .map(n => n[0].toUpperCase())
      .join("");

  const generateNdaPdf = async () => {
    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);
    const fontBytes = await fetch("/fonts/Roboto-Regular.ttf").then(res =>
      res.arrayBuffer()
    );
    const font = await pdfDoc.embedFont(fontBytes);

    const pageSize = [595, 842];
    const marginX = 50;
    const maxWidth = 495;
    const fontSize = 10;
    const lineHeight = 14;
    let pages = [];
    let page = pdfDoc.addPage(pageSize);
    pages.push(page);
    const { height } = page.getSize();
    let y = height - 50;

    const initials = getInitials(username);

    const drawFooter = (page, text) => {
      page.drawText(text, {
        x: marginX,
        y: 30,
        size: 9,
        font,
      });
    };

    const drawParagraph = (rawText, isTitle = false) => {
      const text = normalizeText(rawText);
      const size = isTitle ? 12 : fontSize;
      const spacing = isTitle ? 20 : lineHeight;

      const words = text.split(" ");
      let line = "";

      for (const word of words) {
        const testLine = line + word + " ";
        const width = font.widthOfTextAtSize(testLine, size);

        if (width > maxWidth) {
          if (y < 60) {
            page = pdfDoc.addPage(pageSize);
            pages.push(page);
            y = height - 50;
          }

          page.drawText(line.trim(), { x: marginX, y, size, font });
          y -= spacing;
          line = word + " ";
        } else {
          line = testLine;
        }
      }

      if (line.trim()) {
        if (y < 60) {
          page = pdfDoc.addPage(pageSize);
          pages.push(page);
          y = height - 50;
        }

        page.drawText(line.trim(), { x: marginX, y, size, font });
        y -= spacing;
      }

      y -= 10;
    };

    // ✅ NDA body
    NDA_PDF_TEXT({ today, username }).forEach((text, index) => {
      drawParagraph(text, index === 0);
    });

    y -= 30;

    // ✅ Signature block (LAST PAGE)
    drawParagraph("Accepted By:");
    drawParagraph(`Name: ${username}`);
    drawParagraph(`Email: ${email}`);
    drawParagraph(`Date: ${today}`);
    drawParagraph("Status: NDA ACCEPTED");

    // ✅ Footer logic
    pages.forEach((p, index) => {
      const isLastPage = index === pages.length - 1;

      if (isLastPage) {
        drawFooter(p, `${username} | ${today}`);
      } else {
        drawFooter(p, `Initials: ${initials}`);
      }
    });

    return await pdfDoc.save();
  };

  const previewNdaPdf = async () => {
    try {
      const pdfBytes = await generateNdaPdf();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      window.open(url, "_blank");
    } catch (err) {
      console.error("Error previewing NDA PDF:", err);
    }
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const payload = {
        email,
        requestedFor: requestedFor ?? null,
        requestedForUserName: requestedForUserName ?? null,
        applicationType: application,
        regions: {},
        //mrpControllerList: mrpController,
        confidentialAccess: currentRequest.confidentialAccess
      };

      currentRequest.region.forEach((regionName) => {
        // Skip region if user already has full access
        // if (fullyAccessibleRegions.includes(regionName)) {
        //   return;
        // }

        const plantsForRegion = (fieldsData.plants[regionName] || [])
          .filter(plantObj => currentRequest.plant.includes(plantObj.value))
          .map(plantObj => plantObj.value.split("-")[0]);

        // Skip region if no plants selected
        if (plantsForRegion.length > 0) {
          payload.regions[regionName] = plantsForRegion;
        }
      });

      //const res = await raiseAccessRequest(payload);
      const pdfBytes = await generateNdaPdf();
      const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });

      // 🔹 3. Send BOTH JSON + PDF
      const formData = new FormData();
      formData.append(
        "requestData",
        new Blob([JSON.stringify(payload)], { type: "application/json" })
      );
      formData.append(
        "ndaPdf",
        new Blob([pdfBytes], { type: "application/pdf" }),
        "NDAForm.pdf"
      );

      const res = await raiseAccessRequest(formData);

      if (res?.status === 200) {
        setSnackbarMessage(res?.message || "Request Submitted Successfully");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      }
      else {
        setSnackbarMessage(res?.message || "Error Submitting Request");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.error("Error submitting access request:", err);
      setSnackbarMessage("Something went wrong while submitting request");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setCurrentRequest(initialRequestState);
    setNdaDecision("Not Accepted");
    setShowPreviewSummary(false);
  };

  return (
    <>
      {submitting && <SectionSpinner source="AccessRequestForm" />}
      <div>
        <TopSection breadcrumbArray={breadcrumbArray} hideHeader={true} headerName={""} hideTitle={true} />

        {/* <Box sx={{ display: "flex", minHeight: "100vh", p: 2, fontFamily: "Inter", boxSizing: "border-box" }}> */}

        {/* <Box sx={{ flexGrow: 1, width: '100%', mx: "auto", pr: 1 }}> */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, my: 4 }}>
          {/* <Paper elevation={3} sx={{ width: '95%', height: 525, px: 2, pt: 5, borderRadius: 2, backgroundColor: "#faf5f5ff", boxShadow: "0 4px 6px rgba(0,0,0,0.05)", flexShrink: 0, overflowY: "auto" }}> */}
          {/* <Typography variant="h6" fontWeight={700} color="#000">Access Request Management</Typography> */}
          <Box mt={0} mb={0} sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2.5,
            width: INPUT_WIDTH,
            position: "relative"
          }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="application-label">Application</InputLabel>
              <Select
                labelId="application-label"
                id="application"
                value="Command Center"
                label="Application"
                disabled
                sx={{ color: "#000" }}
              >
                {(fieldsData.applications || []).map((app) => (
                  <MenuItem key={app} value={app}>
                    {app}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body1" fontWeight={700} mb={0} color="#000">Requested for (Optional)</Typography>
            <TextField
              label="User Name"
              size="small"
              fullWidth
              value={requestedForUserName}
              onChange={handleRequestedForUserNameChange}
              placeholder="Enter User Name"
            />
            <TextField
              label="Email"
              size="small"
              fullWidth
              value={requestedFor}
              onChange={handleRequestedForChange}
              placeholder="Enter Email"
            />
          </Box>

          <Typography variant="body1" fontWeight={700} mb={0} color="#000">Access Level</Typography>

          <Box display="flex" flexDirection="column" gap={2} alignItems="center" mb={0}>
            <MultiSelectCheckbox
              label="Region"
              value={region}
              options={fieldsData.regions}
              onChange={handleMultiSelectChange("region")}
              placeholder="Any Region"
              width={INPUT_WIDTH} />

            <MultiSelectCheckbox
              label="Plant"
              value={plant}
              options={availablePlants}
              onChange={handleMultiSelectChange("plant")}
              disabled={region.length === 0}
              placeholder="Any Plant"
              width={INPUT_WIDTH} />

            {/* <MultiSelectCheckbox
              label="MRP Controller"
              value={currentRequest.mrpController || []}
              options={availableMrpControllers}
              onChange={handleMultiSelectChange("mrpController")}
              disabled={availableMrpControllers.length === 0}
              placeholder="Any MRP Controller"
              width={INPUT_WIDTH} /> */}
          </Box>

          <Typography variant="body1" fontWeight={700} mb={0} color="#000">Confidential Level</Typography>

          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <Button
              variant="contained"
              onClick={() => setOpenNdaModal(true)}
              disabled={requestedForUserName.trim().length > 0 || requestedFor.trim().length > 0}   // Disable if requesting on behalf of another user
              sx={{
                backgroundColor: "#4b4b4bff",
                height: 30,
                "&:hover": { backgroundColor: "#2d2d2dff" },
              }}
            >
              View Non Disclosure Agreement (NDA)
            </Button>
            {ndaDecision === "Accepted" && (
              <Chip
                icon={<DoneIcon fontSize="small" />}
                label="NDA ACCEPTED"
                color="success"
                size="medium"
              />
            )}
            {ndaDecision === "Declined" && (
              <Chip
                icon={<PriorityHighIcon fontSize="small" />}
                label="NDA DECLINED"
                color="error"
                variant="filled"
              />
            )}
          </Box>
          <Box mb={1}>
            <MultiSelectCheckbox
              label="Confidential"
              value={confidentialAccess}
              options={fieldsData.confidentialFields}
              onChange={handleMultiSelectChange("confidentialAccess")}
              placeholder="No Confidential Access"
              disabled={!ndaAccepted}                        // Disable until NDA is accepted
              width={INPUT_WIDTH}
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button variant="contained" onClick={handlePreview}
              disabled={(plant.length === 0 || region.length === 0 /*|| mrpController.length === 0*/) && (confidentialAccess.length === 0)}
              sx={{
                backgroundColor: "#4b4b4bff",
                "&:hover": { backgroundColor: "#363636ff" }
              }}>
              Preview
            </Button>
            <Button variant="contained" onClick={handleReset}
              disabled={(plant.length === 0 || region.length === 0 /*|| mrpController.length === 0*/) && (confidentialAccess.length === 0)}
              sx={{
                backgroundColor: "#4b4b4bff",
                "&:hover": { backgroundColor: "#363636ff" }
              }}>
              Reset
            </Button>
            <Button variant="contained" onClick={handleSave}
              disabled={(plant.length === 0 || region.length === 0 /*|| mrpController.length === 0*/) && (confidentialAccess.length === 0)}
              sx={{
                backgroundColor: "#4b4b4bff",
                "&:hover": { backgroundColor: "#363636ff" }
              }}>
              Submit
            </Button>
            {/* <Button
              variant="outlined"
              onClick={previewNdaPdf}
              disabled={!ndaAccepted}
            >
              Preview NDA PDF
            </Button> */}
          </Box>
          {showPreviewSummary && (
            <Paper sx={{ p: 2, mt: 3, mb: 2, backgroundColor: "#cdcacaff", border: "1px dashed #E5E7EB", position: "relative" }}>
              {/* Close Icon */}
              <IconButton
                size="small"
                onClick={() => setShowPreviewSummary(false)}
                sx={{ position: "absolute", top: 4, right: 4, color: "#333" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" fontWeight={600} color="#1A535C" mb={1}>
                Preview Summary:
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-line", color: "#333" }}
              >
                {previewSummary}
              </Typography>
            </Paper>
          )}
          <Dialog
            open={openNdaModal}
            onClose={() => setOpenNdaModal(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pr: 1
              }}
            >
              Non‑Disclosure Agreement (NDA)
              <IconButton
                onClick={() => setOpenNdaModal(false)}
                size="small"
                sx={{ color: "#555" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ maxHeight: "60vh" }}>
              <Typography variant="body2" paragraph>
                This Non-Disclosure Agreement <strong>(Agreement)</strong> is entered into effective as of <strong>{today} </strong>
                between <strong> The Coca-Cola Company located at One Coca-Cola Plaza NW, Atlanta, GA 30313</strong> and <strong>{username}</strong>, each of which
                will be referred to as a Party and collectively as Parties. The term “Party” will include an affiliate of that Party.
                For purposes of this Agreement, an “Affiliate” of a Party means
                <ol type="a" style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                  <li>direct and indirect subsidiaries;</li>
                  <li>
                    any entity that such Party directly, or indirectly through one or more intermediaries,
                    controls, or is controlled by or is under common control with; or
                  </li>
                  <li>
                    in the case of TCCC, any bottler with whom TCCC has a license, alliance, or distribution
                    agreement and any wholly-owned subsidiary thereof.
                  </li>
                </ol>
              </Typography>

              <Typography variant="body2" paragraph>
                The Parties may disclose or share with each other information, documents, and other confidential or proprietary matters of
                the disclosing Party (Information) in connection with <strong>Confidential Information</strong>.
              </Typography>

              <Typography variant="body2" paragraph>
                The following Information will be considered <strong>Confidential Information</strong>:
                <ol type="a" style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                  <li>Information that the disclosing Party marks or designates as confidential;</li>
                  <li>
                    Information that is not marked or designated as being confidential, but that under the circumstances a reasonable person would believe to be confidential;
                  </li>
                  <li>the fact that this Agreement exists and that the Parties are cooperating with each other for the Purpose; and</li>
                  <li>any notes, analyses, compilations, studies, summaries, and other materials, however documented, containing or based, in whole or in part, on any Confidential Information.</li>
                </ol>
              </Typography>

              <Typography variant="body2" paragraph>
                Each Party receiving Confidential Information will:
                <ol type="a" style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                  <li>restrict the disclosure of Confidential Information only to its employees who have a need to know the Confidential Information to achieve the Purpose;
                  </li>
                  <li>
                    not disclose the Confidential Information to any third party; provided, however,
                    that each Party may disclose Confidential Information to its affiliates,
                    directors, attorneys, and financial advisors who:
                    <ol type="i" style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                      <li>
                        need to know the Confidential Information to assist the Party, or act on its
                        behalf, in relation to the Purpose or to exercise its rights under this
                        Agreement;
                      </li>
                      <li>are informed by the Party of the confidential nature of the Confidential Information; and</li>
                      <li>
                        are subject to confidentiality duties or obligations to the Party that are no
                        less restrictive than the terms and conditions contained in this Agreement;
                      </li>
                    </ol>
                    provided, however, that the Party will be responsible for any breach of this
                    Agreement caused by them;
                  </li>

                  <li>only use the Confidential Information for the Purpose;</li>

                  <li>
                    return all tangible Information (including any copies thereof), and erase all
                    electronic or other intangible forms of the Information, within 30 days of the
                    receipt of a written request from the disclosing Party to do so, provided, however,
                    that the receiving Party may retain one archival copy to the extent required to
                    comply with its legal obligations; and
                  </li>

                  <li>
                    give prompt written notice to the disclosing Party of any unauthorized use or
                    disclosure of the Confidential Information, and reasonably assist the disclosing
                    Party in remedying each unauthorized use or disclosure.
                  </li>
                </ol>
              </Typography>

              <Typography variant="body2" paragraph>
                The obligations in Section 4 above will not apply to Confidential Information that:
                <ol type="a" style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                  <li>is, or subsequently becomes, available to the public through no breach of the receiving Party’s obligations hereunder;</li>
                  <li>the receiving Party can show that such Information was previously known to it as a matter of record at the time of receipt;</li>
                  <li>is subsequently and lawfully obtained from a third party who has obtained the Confidential Information through no breach of the receiving Party’s obligations hereunder; or</li>
                  <li>is disclosed to a third party by the disclosing Party without a corresponding obligation of confidentiality.</li>
                </ol>

              </Typography>
              <Typography variant="body2" paragraph>
                Additionally, a receiving Party will not be in breach of its obligations if it discloses Confidential Information as required by a government agency or by operation of law; provided however that the receiving Party (to the extent permitted by applicable law):
                <ol type="a" style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                  <li>notifies the disclosing Party prior to disclosure to enable the disclosing Party to seek to oppose or restrict the disclosure;</li>
                  <li>reasonably cooperates with any such attempt by the disclosing Party to oppose or restrict the disclosure or to seek a protective order; and</li>
                  <li>only discloses such Confidential Information that is required to be disclosed by applicable law.</li>
                </ol>
              </Typography>
              <Typography variant="body2" paragraph>
                Each Party agrees not to publish or use any advertising, sales promotion, mailing or publicity matter that mentions or implies the names of the other Party or its affiliates or authorized bottlers without the prior written consent of the other Party.
              </Typography>

              <Typography variant="body2" paragraph>
                No agreement regarding the Purpose will be deemed to exist unless and until a definitive written agreement regarding the Purpose has been signed by each of the Parties. Neither Party is obligated to enter into a definitive written agreement, and neither Party is precluded from considering the same or similar goods or services from third parties. Neither Party will be obligated to:
                <ol type="a" style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                  <li>pay or otherwise provide a benefit to the other Party;</li>
                  <li>take or refrain from taking any action, except as expressly set forth in this Agreement; or</li>
                  <li>disclose any Information.</li>
                </ol>

              </Typography>

              <Typography variant="body2" paragraph>
                Each disclosing Party represents that it has the lawful right to disclose any Information that it discloses to the receiving Party. Each Party makes no further representations of any kind with respect to any Information that it discloses, and any such Information is provided on an “as is” basis.
              </Typography>

              <Typography variant="body2" paragraph>
                This Agreement and its interpretation, and any disputes related to or arising out of this Agreement, will be governed by the laws of the State of Georgia. The courts in the city of Atlanta, Georgia having jurisdiction will adjudicate any such disputes. Each party hereby irrevocably submits to the exclusive jurisdiction of such courts in any such suit, action or proceeding, and each party consents to the venue of such courts.
              </Typography>

              <Typography variant="body2" paragraph>
                A breach of this Agreement by a receiving Party will cause the disclosing Party irreparable injury for which monetary damages will not provide adequate compensation and, in addition to any other remedy, the disclosing Party will be entitled to seek injunctive relief against such breach or threatened breach without proving actual damages.
              </Typography>

              <Typography variant="body2" paragraph>
                Each Party, in connection with this Agreement, will not engage with:
                <ol type="a" style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                  <li>the government of, or any entity, group or individual within, any country that is the target of any laws administered by the U.S. Office of Foreign Assets Control, Department of the Treasury (OFAC) or any other governmental entity around the world imposing economic sanctions (collectively Embargoed Country); and</li>
                  <li>any government, entity, group, or individual who is named on the OFAC List of Specially Designated Nationals and Blocked Persons or other similar lists maintained by any governmental entity (collectively Sanctioned Party).</li>
                </ol>
              </Typography>

              <Typography variant="body2" paragraph>
                Each Party represents and warrants that it is not:
                <ol type="a" style={{ paddingLeft: '2rem', marginTop: '0.5rem' }}>
                  <li>a Sanctioned Party;</li>
                  <li>owned or controlled by, or acting for or on behalf of, a Sanctioned Party; or</li>
                  <li>directly or indirectly owned, controlled by, or acting on behalf of an Embargoed Country.</li>
                  Should a Party become designated a Sanctioned Party, or become associated, controlled, or owned by a
                  Sanctioned Party or Embargoed Country, any agreement between the Parties will terminate automatically.
                </ol>
              </Typography>
              <Typography variant="body2" paragraph>
                Neither Party will be entitled to assign this Agreement to a third party.
              </Typography>

              <Typography variant="body2" paragraph>
                This Agreement may be executed in any number of counterparts (including without limitation signature via DocuSign or similar e-signature service or by facsimile transmission), each of which will be deemed an original, but all of which taken together will constitute one single agreement between the Parties. The person signing on behalf of each Party represents that he or she is authorized to execute this Agreement on behalf of such Party and has the authority to bind such Party to the terms and conditions of this Agreement.
              </Typography>
              <Typography variant="body2" paragraph>
                This Agreement represents the entire agreement of the Parties and it supersedes all prior communications, agreements, and understandings between the Parties relating to the subject matter hereof. This Agreement may not be modified, amended, or waived except by a written agreement signed by each Party. If a court of competent jurisdiction determines that a provision of this Agreement is unenforceable, this Agreement will be interpreted as if such unenforceable provision is not part of this Agreement.

              </Typography>

              <Typography variant="body2" paragraph>
                The initial term of this Agreement will be for a term of three (3) years. Thereafter, the term of this Agreement will renew for successive one-year periods until terminated by either Party. Either Party may terminate this Agreement by providing at least 30 days’ prior written notice to the individual signing on behalf of the other Party. Sections 4, 5, 6, 7 and 8 will survive termination of this Agreement and will continue until the information is no longer considered Confidential Information; provided however that with respect to any Confidential Information that constitutes a trade secret, Sections 4, 5, 6, 7, and 8 will continue to apply until such time as that Confidential Information is no longer protected as a trade secret under applicable law.
              </Typography>
            </DialogContent>
            {/* <DialogContent dividers sx={{ maxHeight: "60vh" }}>
  {NDA_CONTENT({ today, username })}
</DialogContent> */}

            <DialogActions>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setNdaAccepted(false);
                  setNdaDecision("Declined");
                  setOpenNdaModal(false);
                }}
              >
                Decline
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setNdaAccepted(true);
                  setNdaDecision("Accepted");
                  setOpenNdaModal(false);
                }}
              >
                Accept
              </Button>
            </DialogActions>
          </Dialog>
          <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Box>
        {/* </Box> */}
        {/* </Box > */}
      </div>
    </>
  );
};

export default AccessRequestForm;
